import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import buildingImg from '../assets/building.png';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All Gallery');

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const { data, error } = await supabase
        .from('gallery')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setImages(data || []);
    } catch (err) {
      console.error('Error fetching gallery:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group images by category
  const groupedImages = images.reduce((acc, img) => {
    if (!acc[img.category]) acc[img.category] = [];
    acc[img.category].push(img);
    return acc;
  }, {});

  // Extract unique categories for tabs
  const categories = Object.keys(groupedImages);
  
  // Decide which sections to render
  const sectionsToRender = activeTab === 'All Gallery' ? categories : [activeTab];

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      
      {/* 1. Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left: Text Content */}
          <div className="order-2 lg:order-1">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs sm:text-sm font-bold tracking-widest mb-6">
              <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
              <span className="text-gray-500 material-symbols-outlined text-sm">chevron_right</span>
              <span className="text-[#D4AF37] uppercase">Gallery</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl font-serif text-white mb-4 tracking-wide uppercase">
              GALLERY
            </h1>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#D4AF37] font-medium mb-6 uppercase tracking-wide leading-tight">
              CRAFTING SPACES.<br/>INSPIRING LIVES.
            </h2>
            
            <p className="text-sm sm:text-base text-gray-400 mb-8 max-w-md leading-relaxed">
              Take a visual journey through our iconic projects, landmarks, events, and milestones that reflect our commitment to excellence and innovation.
            </p>
          </div>

          {/* Right: Featured Hero Image */}
          <div className="order-1 lg:order-2 w-full h-[300px] sm:h-[400px] lg:h-[450px] rounded-sm overflow-hidden shadow-2xl relative border border-gray-800">
            <img 
              src={images.length > 0 ? images[0].image_url : buildingImg} 
              alt="Gallery Highlight" 
              className="w-full h-full object-cover"
            />
            {/* Overlay to ensure text pops if you add any */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#041424]/80 to-transparent"></div>
          </div>

        </div>
      </section>

      {/* 2. Filter Bar */}
      <section className="w-full border-b border-gray-800 sticky top-0 z-40 bg-[#041424]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto no-scrollbar">
          <div className="flex items-center min-w-max gap-8 lg:gap-12">
            
            <button 
              onClick={() => setActiveTab('All Gallery')}
              className={`flex items-center gap-2 py-5 font-bold text-[10px] sm:text-xs uppercase tracking-widest whitespace-nowrap transition-colors ${activeTab === 'All Gallery' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-gray-500 hover:text-white'}`}
            >
              <span className="material-symbols-outlined text-lg">grid_view</span>
              All Gallery
            </button>
            
            {categories.map((cat, idx) => (
              <button 
                key={idx}
                onClick={() => setActiveTab(cat)}
                className={`flex items-center gap-2 py-5 font-bold text-[10px] sm:text-xs uppercase tracking-widest whitespace-nowrap transition-colors ${activeTab === cat ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-gray-500 hover:text-white'}`}
              >
                <span className="material-symbols-outlined text-lg">image</span>
                {cat}
              </button>
            ))}

          </div>
        </div>
      </section>

      {/* 3. Main Gallery Sections */}
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">
        
        {loading ? (
           <div className="flex justify-center py-20"><span className="material-symbols-outlined animate-spin text-4xl text-[#D4AF37]">sync</span></div>
        ) : categories.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            <span className="material-symbols-outlined text-6xl mb-4 opacity-50">photo_library</span>
            <p>Our gallery is currently being updated. Check back soon!</p>
          </div>
        ) : (
          sectionsToRender.map((cat) => (
            <section key={cat}>
              <div className="flex items-center justify-between mb-6 border-b border-gray-800 pb-4">
                <h3 className="text-lg sm:text-xl font-bold text-[#D4AF37] uppercase tracking-widest">{cat}</h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {groupedImages[cat]?.map((img) => (
                  <div key={img.id} className="relative h-48 sm:h-56 lg:h-72 rounded-sm overflow-hidden group border border-gray-800 cursor-pointer">
                    <img src={img.image_url} alt={img.title || cat} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-[#041424]/20 group-hover:bg-transparent transition-colors duration-500"></div>
                    {img.title && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#041424] to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <p className="text-xs text-white font-medium">{img.title}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))
        )}

      </main>

      {/* 4. CTA Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-[#06121e] border border-gray-800 rounded-sm p-8 sm:p-10 flex flex-col md:flex-row justify-between items-center gap-8 relative overflow-hidden">
          
          {/* Subtle Background Watermark */}
          <span className="material-symbols-outlined absolute -right-10 -bottom-10 text-[200px] text-gray-800/20 pointer-events-none transform -rotate-12">
            photo_camera
          </span>

          {/* Left Text */}
          <div className="flex items-center gap-6 relative z-10 w-full md:w-auto">
            <div className="w-16 h-16 shrink-0 rounded-full border border-[#D4AF37]/50 flex items-center justify-center bg-[#041424]">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">photo_camera</span>
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl text-white font-light mb-2">Every Image Tells a Story of Excellence</h3>
              <p className="text-gray-400 text-sm">Explore our journey of creating iconic spaces and delivering unmatched value.</p>
            </div>
          </div>

          {/* Right Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 relative z-10 w-full md:w-auto shrink-0">
            <Link to="/brochure" className="bg-[#D4AF37] text-[#041424] px-6 py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Download Gallery Brochure <span className="material-symbols-outlined text-base">download</span>
            </Link>
            <Link to="/book-visit" className="border border-white text-white px-6 py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white hover:text-[#041424] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Book a Site Visit <span className="material-symbols-outlined text-base">event</span>
            </Link>
          </div>

        </div>
      </section>

    </div>
  );
};

export default Gallery;